"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SolicitudAdscripcionDetallesSchema = exports.ItemAdscripcionSchema = exports.MOTIVO_ITEM_MAX_LARGO = exports.EstadoAdscripcionSchema = exports.MAX_UNIDADES_ADICIONALES = void 0;
exports.validarEstadoAdscripcion = validarEstadoAdscripcion;
exports.mensajeViolacionAdscripcion = mensajeViolacionAdscripcion;
exports.diffAdscripcion = diffAdscripcion;
exports.itemPideMotivo = itemPideMotivo;
exports.itemsSinMotivo = itemsSinMotivo;
exports.motivosDeItems = motivosDeItems;
exports.conMotivos = conMotivos;
exports.textoCortoItemAdscripcion = textoCortoItemAdscripcion;
exports.textoMotivosAdscripcion = textoMotivosAdscripcion;
exports.lineasSinRespaldo = lineasSinRespaldo;
exports.lineasACerrar = lineasACerrar;
exports.ordenarAdicionales = ordenarAdicionales;
exports.resolverAdscripcion = resolverAdscripcion;
exports.parsearSolicitudAdscripcion = parsearSolicitudAdscripcion;
exports.describirItemAdscripcion = describirItemAdscripcion;
exports.calcularEfectoAdscripcion = calcularEfectoAdscripcion;
const zod_1 = require("zod");
const estructura_institucional_1 = require("./estructura-institucional");
// ─── Multi-unit adscription: pure core shared by web and API ──────────────────
//
// A researcher has ONE principal unit and up to MAX_UNIDADES_ADICIONALES
// additional units. Everything here is pure (no I/O, no dates) so the API can
// validate/apply with the same rules the web previews. Spanish identifiers are
// the domain vocabulary; do not translate them.
// ──────────────────────────────────────────────────────────────────────────────
/** Single source of the additional-unit cap: server validation, UI limits and messages. */
exports.MAX_UNIDADES_ADICIONALES = 3;
const IdSchema = zod_1.z.number().int().positive();
/** A unit id: same shape as any other id, named apart for readability. */
const IdUnidadSchema = IdSchema;
/** Target (or current) unit state: the principal plus the additional units. */
exports.EstadoAdscripcionSchema = zod_1.z.object({
    idUnidadPrincipal: IdUnidadSchema,
    idUnidadesAdicionales: zod_1.z
        .array(IdUnidadSchema)
        .max(exports.MAX_UNIDADES_ADICIONALES, mensajeViolacionAdscripcion('tope')),
});
/**
 * Validates a unit state. Returns EVERY violated rule, in a fixed order (an empty array
 * means valid):
 *  - `principal_en_adicionales`: the principal is also listed as additional.
 *  - `duplicada`: an additional unit is listed twice.
 *  - `tope`: more additional units than the cap (`MAX_UNIDADES_ADICIONALES` unless overridden).
 *  - `otra_repetida`: the generic "Otra" unit appears more than once in total
 *    (principal + additional). It is reported on top of the two rules above when the
 *    repeated unit is "Otra".
 */
function validarEstadoAdscripcion(estado, opciones = {}) {
    const tope = opciones.tope ?? exports.MAX_UNIDADES_ADICIONALES;
    const { idUnidadPrincipal, idUnidadesAdicionales } = estado;
    const violaciones = [];
    if (idUnidadesAdicionales.includes(idUnidadPrincipal)) {
        violaciones.push('principal_en_adicionales');
    }
    if (new Set(idUnidadesAdicionales).size !== idUnidadesAdicionales.length) {
        violaciones.push('duplicada');
    }
    if (idUnidadesAdicionales.length > tope) {
        violaciones.push('tope');
    }
    if ([idUnidadPrincipal, ...idUnidadesAdicionales].filter(estructura_institucional_1.esUnidadOtra).length > 1) {
        violaciones.push('otra_repetida');
    }
    return violaciones;
}
/**
 * The Spanish, user-facing message of a violation code: the ONE place that words the rules, so the
 * server refusals, the form, the live preview and the admin screen say the same thing. The cap in
 * the `tope` message comes from `MAX_UNIDADES_ADICIONALES` (or the same `tope` override that
 * `validarEstadoAdscripcion` takes), never from a hard-coded number.
 */
function mensajeViolacionAdscripcion(violacion, opciones = {}) {
    switch (violacion) {
        case 'principal_en_adicionales':
            return 'La unidad principal no puede repetirse como unidad adicional.';
        case 'duplicada':
            return 'No se puede repetir una unidad adicional.';
        case 'tope': {
            const tope = opciones.tope ?? exports.MAX_UNIDADES_ADICIONALES;
            return `Se permiten como máximo ${tope === 1 ? 'una unidad adicional' : `${tope} unidades adicionales`}.`;
        }
        case 'otra_repetida':
            return 'Solo puede haber una unidad "Otra" en total.';
    }
}
/**
 * Items that take `base` to `objetivo`, in a fixed order: `principal`, `conservar-*`,
 * `agregar-*` (target order), `quitar-*` (base order). Both states are expected to be
 * valid (`validarEstadoAdscripcion` first); identical states give `[]`.
 *
 * Promoting an existing additional unit to principal yields ONLY the `principal` item:
 * the promoted unit gets neither an `agregar` (it is already active) nor a `quitar`
 * (it stops being additional because it becomes the principal).
 */
function diffAdscripcion(base, objetivo) {
    const items = [];
    const cambiaPrincipal = objetivo.idUnidadPrincipal !== base.idUnidadPrincipal;
    if (cambiaPrincipal) {
        items.push({
            id: 'principal',
            tipo: 'cambiar_principal',
            idUnidad: objetivo.idUnidadPrincipal,
            desde: base.idUnidadPrincipal,
            ...(!base.idUnidadesAdicionales.includes(objetivo.idUnidadPrincipal) && { nueva: true }),
        });
        if (objetivo.idUnidadesAdicionales.includes(base.idUnidadPrincipal)) {
            items.push({
                id: `conservar-${base.idUnidadPrincipal}`,
                tipo: 'conservar_anterior',
                idUnidad: base.idUnidadPrincipal,
                dependeDe: 'principal',
            });
        }
    }
    for (const idUnidad of objetivo.idUnidadesAdicionales) {
        const yaActiva = base.idUnidadesAdicionales.includes(idUnidad);
        const esPrincipalAnterior = cambiaPrincipal && idUnidad === base.idUnidadPrincipal;
        if (!yaActiva && !esPrincipalAnterior) {
            items.push({ id: `agregar-${idUnidad}`, tipo: 'agregar_adicional', idUnidad });
        }
    }
    for (const idUnidad of base.idUnidadesAdicionales) {
        const sigue = objetivo.idUnidadesAdicionales.includes(idUnidad);
        const fuePromovida = idUnidad === objetivo.idUnidadPrincipal;
        if (!sigue && !fuePromovida) {
            items.push({ id: `quitar-${idUnidad}`, tipo: 'quitar_adicional', idUnidad });
        }
    }
    return items;
}
// ─── One reason per structural change ─────────────────────────────────────────
/** Longest reason of ONE change. */
exports.MOTIVO_ITEM_MAX_LARGO = 2000;
/**
 * Whether the researcher must say why they ask for this change: every structural change does, except the derived
 * `conservar_anterior` ("keep the old principal as additional"), which follows the principal change.
 */
function itemPideMotivo(item) {
    return item.tipo !== 'conservar_anterior';
}
const motivoLimpio = (motivo) => motivo?.trim() ?? '';
/** The items that ask for a reason and have none (missing or blank), in item order. */
function itemsSinMotivo(items, motivos) {
    return items.filter((item) => itemPideMotivo(item) && motivoLimpio(motivos?.[item.id]) === '');
}
/** The reasons that count: trimmed and non-empty, of the items that ask for one (anything else is dropped). */
function motivosDeItems(items, motivos) {
    const resultado = {};
    for (const item of items) {
        const motivo = motivoLimpio(motivos?.[item.id]);
        if (itemPideMotivo(item) && motivo !== '')
            resultado[item.id] = motivo;
    }
    return resultado;
}
/** The items with their trimmed reason attached (only the ones that ask for it); the others come back as they are. Pure: the input is not mutated. */
function conMotivos(items, motivos) {
    const validos = motivosDeItems(items, motivos);
    return items.map((item) => (item.id in validos ? { ...item, motivo: validos[item.id] } : item));
}
/** Short Spanish wording of one change, with the unit between angle quotes: the modal's tooltip and the stored reasons use it. */
function textoCortoItemAdscripcion(item, nombreDe) {
    switch (item.tipo) {
        case 'cambiar_principal':
            return item.nueva
                ? `Agregar «${nombreDe(item.idUnidad)}» como nueva unidad principal`
                : `Cambiar la unidad principal a «${nombreDe(item.idUnidad)}»`;
        case 'conservar_anterior':
            return `Conservar «${nombreDe(item.idUnidad)}» como unidad adicional`;
        case 'agregar_adicional':
            return `Agregar «${nombreDe(item.idUnidad)}»`;
        case 'quitar_adicional':
            return `Quitar «${nombreDe(item.idUnidad)}»`;
    }
}
/**
 * The reasons of a request as plain text, one line per item that has one (`Agregar «X»: <motivo>`), in item order. It is
 * what readers of the single `justificacion` / `descripcion` / `detalles.motivo` keep getting. Line breaks inside a reason
 * collapse to a space so a line stays one item.
 */
function textoMotivosAdscripcion(items, nombres) {
    const nombreDe = (idUnidad) => nombres.get(idUnidad)?.trim() || `Unidad ${idUnidad}`;
    return items
        .flatMap((item) => {
        const motivo = (item.motivo ?? '').replace(/\s+/g, ' ').trim();
        return motivo === '' ? [] : [`${textoCortoItemAdscripcion(item, nombreDe)}: ${motivo}`];
    })
        .join('\n');
}
/** A unit backs a line when it owns it or is a centro that offers it. */
function respaldaLinea(linea, unidades) {
    return ((linea.idUnidadDuena !== null && unidades.has(linea.idUnidadDuena)) ||
        linea.idCentros.some((idCentro) => unidades.has(idCentro)));
}
const unidadesDelEstado = (estado) => [
    estado.idUnidadPrincipal,
    ...estado.idUnidadesAdicionales,
];
/**
 * Plain reachability: ids of the held lines that NO unit in `unidades` backs, in input order.
 * Ownerless (transversal) lines are never returned. This is only the primitive: it says nothing
 * about which lines a unit CHANGE closes (it returns unrelated orphans too). To decide what a
 * change closes, use `lineasACerrar`.
 */
function lineasSinRespaldo(lineas, unidades) {
    const restantes = new Set(unidades);
    return lineas
        .filter((linea) => linea.idUnidadDuena !== null && !respaldaLinea(linea, restantes))
        .map(({ idLinea }) => idLinea);
}
/**
 * The ONE rule for the held lines a unit change closes; the server (admin edit, request approval,
 * onboarding) and the live preview all use it, so they cannot diverge.
 *
 * A held line closes only when it is backed by at least one DEPARTING unit (in `actual`, not in
 * `final`) and by no REMAINING unit (in `final`), owner or offering centro, dates ignored. So:
 *  - nothing closes when no unit departs (adding an additional, promoting while keeping the previous
 *    principal, an unchanged state);
 *  - a line no departing unit backs is never closed by a unit change, even if it is unbacked
 *    (a legacy orphan is not swept);
 *  - ownerless lines (98, 99) are never closed.
 *
 * `actual` is null/undefined on a first assignment (nothing is current, so nothing departs).
 * Ids come back in input order.
 */
function lineasACerrar(actual, final, lineas) {
    if (!actual)
        return [];
    const restantes = new Set(unidadesDelEstado(final));
    const salen = new Set(unidadesDelEstado(actual).filter((idUnidad) => !restantes.has(idUnidad)));
    if (salen.size === 0)
        return [];
    return lineas
        .filter((linea) => linea.idUnidadDuena !== null && respaldaLinea(linea, salen) && !respaldaLinea(linea, restantes))
        .map(({ idLinea }) => idLinea);
}
// ─── Position of the units a request adds ─────────────────────────────────────
/**
 * The order of the additional units once some of them are NEW (an approved request adds them). The ONE rule
 * for "where does the new unit go", shared by the resolver (the `final` state) and the API mirror rows, so the order
 * the researcher asked for is the order that gets saved.
 *
 *  - `conservadas`: the additional units that stay, in the order they have now. They keep it: a request never reorders
 *    what the researcher already has (that is an immediate change of its own).
 *  - `nuevas`: the units being added.
 *  - `solicitado`: the order of the additional units the request asked for (its target). Only the units that are really
 *    applied count, so one the admin rejected is simply skipped and the others count from what is left. A new unit
 *    takes the INDEX it has in that list, placed left to right; one the request does not list goes last.
 *
 * Without `solicitado` the new units go after the kept ones, in the given order. Pure: the inputs are not mutated.
 */
function ordenarAdicionales(conservadas, nuevas, solicitado) {
    const lista = [...conservadas];
    const porColocar = new Set(nuevas);
    const aplicadas = new Set([...conservadas, ...nuevas]);
    const pedido = [...new Set(solicitado ?? [])].filter((idUnidad) => aplicadas.has(idUnidad));
    pedido.forEach((idUnidad, indice) => {
        if (!porColocar.delete(idUnidad))
            return;
        lista.splice(indice, 0, idUnidad);
    });
    for (const idUnidad of nuevas) {
        if (porColocar.delete(idUnidad))
            lista.push(idUnidad);
    }
    return lista;
}
// ─── Resolving a request (full approval or per item) ──────────────────────────
/** Fixed reporting order of the violations (the order `validarEstadoAdscripcion` returns them in). */
const ORDEN_VIOLACIONES = [
    'principal_en_adicionales',
    'duplicada',
    'tope',
    'otra_repetida',
];
/** Union of several violation lists, without repeats, in the fixed reporting order. */
function unirViolaciones(...listas) {
    return ORDEN_VIOLACIONES.filter((violacion) => listas.some((lista) => lista.includes(violacion)));
}
const MOTIVO_PRINCIPAL_CAMBIO = 'La unidad principal cambió después de crear la solicitud.';
const MOTIVO_YA_VIGENTE = 'La unidad ya está vigente en la adscripción.';
const MOTIVO_YA_NO_ADICIONAL = 'La unidad ya no es una unidad adicional.';
const MOTIVO_CASCADA = 'Depende del cambio de unidad principal, que no se aplicó.';
/** Reason an item no longer applies to the CURRENT state, or null when it still does. */
function motivoDesactualizado(item, actual) {
    switch (item.tipo) {
        case 'cambiar_principal':
            return actual.idUnidadPrincipal === item.desde ? null : MOTIVO_PRINCIPAL_CAMBIO;
        case 'agregar_adicional': {
            const vigente = actual.idUnidadPrincipal === item.idUnidad || actual.idUnidadesAdicionales.includes(item.idUnidad);
            return vigente ? MOTIVO_YA_VIGENTE : null;
        }
        case 'quitar_adicional':
            return actual.idUnidadesAdicionales.includes(item.idUnidad) ? null : MOTIVO_YA_NO_ADICIONAL;
        case 'conservar_anterior':
            // It depends on the principal change: it becomes void through the cascade, never stale by itself.
            return null;
    }
}
/**
 * Recomputes the final unit state by applying the approved items to the CURRENT state.
 *
 *  1. Explicit decisions (a missing decision means approved; unknown ids are ignored).
 *  2. Stale items, evaluated against `actual`, are auto-rejected (`desactualizado`). An item the
 *     admin already rejected keeps the admin's decision and reason.
 *  3. Cascade: without an approved principal change, "conservar" items are voided (`cascada`),
 *     even if the admin approved them. Independent items (agregar/quitar) are unaffected.
 *  4. The approved items are applied to `actual`. Rejecting "conservar" while approving the
 *     principal change makes the previous principal depart. The units that were already additional keep
 *     their order; with `ordenAdicionales` the new ones sit where the request put them, else last.
 *  5. The result (and the requested `objetivo`, when given) is validated and the violations are
 *     REPORTED, never trimmed.
 */
function resolverAdscripcion(entrada) {
    const { actual, items } = entrada;
    const explicitas = new Map(entrada.decisiones.map((decision) => [decision.id, decision]));
    const decisiones = items.map((item) => {
        const explicita = explicitas.get(item.id);
        if (explicita && !explicita.aprobado) {
            return { id: item.id, aprobado: false, motivo: explicita.motivo ?? null, automatico: null };
        }
        const desactualizado = motivoDesactualizado(item, actual);
        if (desactualizado) {
            return { id: item.id, aprobado: false, motivo: desactualizado, automatico: 'desactualizado' };
        }
        return { id: item.id, aprobado: true, motivo: explicita?.motivo ?? null, automatico: null };
    });
    const cambioPrincipalAprobado = items.some((item, indice) => item.tipo === 'cambiar_principal' && decisiones[indice].aprobado);
    items.forEach((item, indice) => {
        if (item.tipo === 'conservar_anterior' && decisiones[indice].aprobado && !cambioPrincipalAprobado) {
            decisiones[indice] = { id: item.id, aprobado: false, motivo: MOTIVO_CASCADA, automatico: 'cascada' };
        }
    });
    const aprobados = items.filter((_, indice) => decisiones[indice].aprobado);
    const rechazados = items.filter((_, indice) => !decisiones[indice].aprobado);
    let principal = actual.idUnidadPrincipal;
    for (const item of aprobados) {
        if (item.tipo === 'cambiar_principal')
            principal = item.idUnidad;
    }
    // A promoted additional stops being additional; otherwise `actual` is taken as is, so a
    // corrupted current state surfaces in `violaciones` instead of being silently repaired.
    let adicionales = principal === actual.idUnidadPrincipal
        ? [...actual.idUnidadesAdicionales]
        : actual.idUnidadesAdicionales.filter((idUnidad) => idUnidad !== principal);
    const nuevas = [];
    const agregar = (idUnidad) => {
        if (idUnidad !== principal && !adicionales.includes(idUnidad)) {
            adicionales.push(idUnidad);
            nuevas.push(idUnidad);
        }
    };
    for (const item of aprobados) {
        if (item.tipo === 'conservar_anterior' || item.tipo === 'agregar_adicional')
            agregar(item.idUnidad);
        if (item.tipo === 'quitar_adicional') {
            adicionales = adicionales.filter((idUnidad) => idUnidad !== item.idUnidad);
        }
    }
    if (entrada.ordenAdicionales && nuevas.length > 0) {
        adicionales = ordenarAdicionales(adicionales.filter((idUnidad) => !nuevas.includes(idUnidad)), nuevas, entrada.ordenAdicionales);
    }
    const final = { idUnidadPrincipal: principal, idUnidadesAdicionales: adicionales };
    const sinCambios = final.idUnidadPrincipal === actual.idUnidadPrincipal &&
        final.idUnidadesAdicionales.length === actual.idUnidadesAdicionales.length &&
        final.idUnidadesAdicionales.every((idUnidad) => actual.idUnidadesAdicionales.includes(idUnidad));
    const violaciones = unirViolaciones(entrada.objetivo ? validarEstadoAdscripcion(entrada.objetivo) : [], validarEstadoAdscripcion(final));
    return { final, decisiones, aprobados, rechazados, sinCambios, violaciones };
}
// ─── Request payload (detalles_json) ──────────────────────────────────────────
/** `<prefijo>-<n>`, typed as the template literal the item ids use. */
function idItem(prefijo) {
    return zod_1.z.string().regex(new RegExp(`^${prefijo}-\\d+$`));
}
/** Id prefix of each item kind that is identified by its unit (`<prefijo>-<idUnidad>`). */
const PREFIJO_ID_ITEM = {
    conservar_anterior: 'conservar',
    agregar_adicional: 'agregar',
    quitar_adicional: 'quitar',
};
/** The reason of an item as stored: optional, so a request stored before the reason moved to its items still parses. */
const MotivoItemGuardadoSchema = zod_1.z.string().optional();
/**
 * An item read back from storage. Its id must be the one `diffAdscripcion` derives from the unit
 * (`agregar-15` for unit 15): a decision persisted per id keeps pointing at the same unit, and a
 * payload whose id and unit disagree is rejected. `principal` is the one fixed id.
 */
exports.ItemAdscripcionSchema = zod_1.z
    .discriminatedUnion('tipo', [
    zod_1.z.object({
        id: zod_1.z.literal('principal'),
        tipo: zod_1.z.literal('cambiar_principal'),
        idUnidad: IdUnidadSchema,
        desde: IdUnidadSchema.nullable(),
        nueva: zod_1.z.boolean().optional(),
        motivo: MotivoItemGuardadoSchema,
    }),
    zod_1.z.object({
        id: idItem('conservar'),
        tipo: zod_1.z.literal('conservar_anterior'),
        idUnidad: IdUnidadSchema,
        dependeDe: zod_1.z.literal('principal'),
    }),
    zod_1.z.object({
        id: idItem('agregar'),
        tipo: zod_1.z.literal('agregar_adicional'),
        idUnidad: IdUnidadSchema,
        motivo: MotivoItemGuardadoSchema,
    }),
    zod_1.z.object({
        id: idItem('quitar'),
        tipo: zod_1.z.literal('quitar_adicional'),
        idUnidad: IdUnidadSchema,
        motivo: MotivoItemGuardadoSchema,
    }),
])
    .refine((item) => item.tipo === 'cambiar_principal' || item.id === `${PREFIJO_ID_ITEM[item.tipo]}-${item.idUnidad}`, { message: 'El identificador del ítem no corresponde a su unidad.' });
/** A state read back from storage: no cap here, so lowering the cap later never strands old requests. */
const EstadoAdscripcionGuardadoSchema = zod_1.z.object({
    idUnidadPrincipal: IdUnidadSchema,
    idUnidadesAdicionales: zod_1.z.array(IdUnidadSchema),
});
const DecisionAdscripcionSchema = zod_1.z.object({
    id: zod_1.z.string(),
    aprobado: zod_1.z.boolean(),
    motivo: zod_1.z.string().nullable().default(null),
    automatico: zod_1.z.enum(['desactualizado', 'cascada']).nullable().default(null),
});
/** What a resolution leaves in `detalles_json`: per-item outcomes, the applied state, the closed lines. */
const ResolucionGuardadaSchema = zod_1.z.object({
    decisiones: zod_1.z.array(DecisionAdscripcionSchema),
    final: EstadoAdscripcionGuardadoSchema,
    lineasCerradas: zod_1.z.array(IdSchema).default([]),
});
const DetallesCambioUnidadSchema = zod_1.z.object({
    unidadActualId: IdUnidadSchema.nullable().optional(),
    unidadSolicitadaId: IdUnidadSchema,
    motivo: zod_1.z.string().nullable().optional(),
});
/**
 * `detalles_json` of a `cambio_unidad` request, version 2: the target state plus the items
 * that reach it. `detalles.unidadSolicitadaId` always holds the target principal, so the
 * legacy parser keeps working if the code is rolled back.
 */
exports.SolicitudAdscripcionDetallesSchema = zod_1.z.object({
    kind: zod_1.z.literal('cambio_unidad'),
    version: zod_1.z.literal(2),
    idInvestigador: IdSchema.optional(),
    justificacion: zod_1.z.string().optional(),
    detalles: DetallesCambioUnidadSchema,
    adscripcion: zod_1.z.object({
        base: EstadoAdscripcionGuardadoSchema,
        objetivo: EstadoAdscripcionGuardadoSchema,
        nombreOtra: zod_1.z.string().max(120).nullable().optional(),
        items: zod_1.z.array(exports.ItemAdscripcionSchema),
    }),
    /** Ids of the pending requests this one replaced. */
    reemplaza: zod_1.z.array(IdSchema).default([]),
    /** Set on a request that was replaced: the id of its replacement. */
    reemplazadaPor: IdSchema.optional(),
    resolucion: ResolucionGuardadaSchema.optional(),
});
/**
 * Version 1 (before multi-unit): only the target unit; no items, no version. A v1 row can still
 * gain the replaced marker (`reemplazadaPor`) or a stored `resolucion` after the deploy, so both are
 * read; any other unknown key is dropped.
 */
const SolicitudCambioUnidadLegacySchema = zod_1.z.object({
    idInvestigador: IdSchema.optional(),
    justificacion: zod_1.z.string().optional(),
    detalles: DetallesCambioUnidadSchema,
    reemplazadaPor: IdSchema.optional(),
    resolucion: ResolucionGuardadaSchema.optional(),
});
/**
 * Reads a stored `detalles_json` as a v2 request.
 *
 * A v2 payload carries its own `base`, so it needs no current state (`actual` is ignored). A legacy
 * v1 payload is upgraded against the CURRENT state `actual`, keeping its old meaning: "change the
 * principal to the target; the previous principal departs; the additional units stay" (a target that
 * is already additional is promoted, not duplicated). The replaced marker and a stored resolution
 * survive the upgrade. The upgraded `items` are derived from `actual`, so a v1 row read after its
 * resolution may show no items: render its outcome from `resolucion`.
 *
 * Returns null when the payload is missing, unreadable, has no target, or is legacy and no `actual`
 * was given (without the state it is upgraded against, its meaning cannot be fixed). A malformed v2
 * is never reinterpreted as legacy.
 */
function parsearSolicitudAdscripcion(detallesJson, actual) {
    if (!detallesJson)
        return null;
    let crudo;
    try {
        crudo = JSON.parse(detallesJson);
    }
    catch {
        return null;
    }
    if (typeof crudo === 'object' && crudo !== null && crudo.version === 2) {
        const v2 = exports.SolicitudAdscripcionDetallesSchema.safeParse(crudo);
        return v2.success ? v2.data : null;
    }
    if (!actual)
        return null;
    const legacy = SolicitudCambioUnidadLegacySchema.safeParse(crudo);
    if (!legacy.success)
        return null;
    const objetivo = {
        idUnidadPrincipal: legacy.data.detalles.unidadSolicitadaId,
        idUnidadesAdicionales: actual.idUnidadesAdicionales.filter((idUnidad) => idUnidad !== legacy.data.detalles.unidadSolicitadaId),
    };
    return {
        kind: 'cambio_unidad',
        version: 2,
        idInvestigador: legacy.data.idInvestigador,
        justificacion: legacy.data.justificacion,
        detalles: legacy.data.detalles,
        adscripcion: { base: actual, objetivo, nombreOtra: null, items: diffAdscripcion(actual, objetivo) },
        reemplaza: [],
        ...(legacy.data.reemplazadaPor !== undefined && { reemplazadaPor: legacy.data.reemplazadaPor }),
        ...(legacy.data.resolucion !== undefined && { resolucion: legacy.data.resolucion }),
    };
}
/** Neutral Spanish description of one item, for the request card, the admin screen and the email. */
function describirItemAdscripcion(item, nombres) {
    const unidad = nombres.get(item.idUnidad) ?? `Unidad ${item.idUnidad}`;
    switch (item.tipo) {
        case 'cambiar_principal':
            return item.nueva ? `Agregar ${unidad} como nueva unidad principal` : `Cambiar la unidad principal a ${unidad}`;
        case 'conservar_anterior':
            return `Conservar ${unidad} como unidad adicional`;
        case 'agregar_adicional':
            return `Agregar ${unidad} como unidad adicional`;
        case 'quitar_adicional':
            return `Quitar ${unidad} de las unidades adicionales`;
    }
}
/**
 * What a request does to the unit state and to the held lines: `resolverAdscripcion` composed with
 * `lineasACerrar`. The server uses it to apply (or refuse) and the web uses it for the live preview,
 * so the preview cannot diverge from what the server does. Paths that have no items (admin direct
 * edit, onboarding) call `lineasACerrar` themselves with the same `actual`/`final`.
 *
 * Only the held lines of a departing unit that no remaining unit backs are closed (see
 * `lineasACerrar`): a change that closes no unit closes no line, and an unrelated orphan is left
 * alone. `lineasCerradas` is only meaningful while `violaciones` is empty: with violations the server
 * applies nothing. Pass `objetivo` (see `EntradaResolucionAdscripcion`) so an invalid requested state
 * shows up in `violaciones`.
 */
function calcularEfectoAdscripcion(entrada) {
    const { final, decisiones, violaciones, sinCambios } = resolverAdscripcion(entrada);
    return {
        final,
        decisiones,
        lineasCerradas: lineasACerrar(entrada.actual, final, entrada.lineasActivas),
        violaciones,
        sinCambios,
    };
}
